import { skipToken } from "@reduxjs/toolkit/query";
import {
  checkFunnelUiVisible,
  checkFunnelUiWritable,
} from "@repo/app-types/entities/funnel";
import { asTeammateId } from "@repo/app-types/entities/teammate";
import { isStaticTeammateAgentIdentity } from "@repo/app-types/entities/teammateAgent";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { EditableText } from "@repo/ui/Inputs/editableText";
import { AlertDialog, AlertDialogVariant } from "@repo/ui/Modal/alertDialog";
import { CenteredModal } from "@repo/ui/Modal/centeredModal";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { PageHeaderFilterBarSlot } from "@repo/ui/Navigation/pageHeaderFilterBarSlot";
import { showToast, ToastVariant } from "@repo/ui/toast/toast";
import { useEffect, useRef, useState, type FC } from "react";
import {
  Navigate,
  Outlet,
  useLocation,
  useMatch,
  useNavigate,
  useParams,
} from "react-router";
import { useVoiceAgentFunnelsQuery } from "../api/entities/funnel.js";
import {
  useDeleteTeammateAgentMutation,
  useGetTeammateAgentQuery,
  useUpdateTeammateAgentMutation,
} from "../api/entities/teammateAgent.js";
import { appPaths } from "../app/appPaths.js";
import { useCollapseNavWhileMounted } from "../app/navCollapseContext.js";
import { TabbedPageLayout } from "../components/layout/pageLayout.js";
import { RouteTabs } from "../components/layout/routeTabs.js";
import { AgentMoreMenu } from "../features/agents/agentMoreMenu.js";
import {
  buildCallFeedbackPrefillMessage,
  useCallFeedbackPrefillFromUrl,
} from "../features/agents/callFeedbackPrefill.js";
import { StaticAgentConfigLayout } from "../features/agents/static/staticAgentConfigLayout.js";
import { useCopilotProps } from "../features/copilot/copilotContext.js";
import { useFeatureFlagVariant } from "../helpers/featureFlags/hooks.js";
import { Can } from "../helpers/teammateAuthorizationContext.js";

export const TeammateAgentPage: FC = () => {
  // Collapse the nav for the whole agent detail area so switching tabs does
  // not shift the layout.
  useCollapseNavWhileMounted();

  const { teammateAgentId } = useParams();
  if (!teammateAgentId) {
    throw new Error("Missing params.");
  }

  const navigate = useNavigate();
  const { search } = useLocation();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Call feedback handed over by "Edit agent" starts an Ask Alpharun thread
  // here, where the page context grounds it in this agent.
  const feedbackPrefill = useCallFeedbackPrefillFromUrl();
  const copilot = useCopilotProps();
  const hasSentFeedbackPrefillRef = useRef(false);
  useEffect(() => {
    if (!feedbackPrefill || !copilot || hasSentFeedbackPrefillRef.current) {
      return;
    }
    hasSentFeedbackPrefillRef.current = true;
    copilot.onOpenChat();
    copilot.onNewThreadSelect({
      initialUserMessageText: buildCallFeedbackPrefillMessage(feedbackPrefill),
    });
  }, [feedbackPrefill, copilot]);

  // Read-only shows the Funnel tab only on an agent that has a funnel.
  const funnelUiVariant = useFeatureFlagVariant(
    FeatureFlagKey.VoiceAgentFunnelUi,
  );
  const isFunnelUiWritable = checkFunnelUiWritable(funnelUiVariant);
  const isFunnelUiReadOnly =
    checkFunnelUiVisible(funnelUiVariant) && !isFunnelUiWritable;
  const { data: funnels } = useVoiceAgentFunnelsQuery(
    isFunnelUiReadOnly ? asTeammateId(teammateAgentId) : skipToken,
  );
  const isFunnelTabShown =
    isFunnelUiWritable || (isFunnelUiReadOnly && Boolean(funnels?.length));

  const { data: teammateAgent, isLoading: isLoadingTeammateAgent } =
    useGetTeammateAgentQuery(teammateAgentId);

  const [deleteTeammateAgent] = useDeleteTeammateAgentMutation();

  const [updateTeammateAgent] = useUpdateTeammateAgentMutation();

  const isConfigurationPage = Boolean(
    useMatch(appPaths.teammateAgentConfiguration.pattern),
  );

  // The static-only tabs live outside the static agent layout's provider for
  // legacy agents, so guard against reaching them directly (bookmark / manual
  // URL) which would otherwise throw from useStaticAgentConfig. The Funnel
  // tab is offered to static agents only. Call the matchers unconditionally so
  // hook order stays stable.
  const isInstructionsPage = Boolean(
    useMatch(appPaths.teammateAgentInstructions.pattern),
  );
  const isResourcesOutputsPage = Boolean(
    useMatch(appPaths.teammateAgentResourcesOutputs.pattern),
  );
  const isFunnelPage = Boolean(useMatch(appPaths.teammateAgentFunnel.pattern));
  const isStaticSectionsPage =
    isInstructionsPage || isResourcesOutputsPage || isFunnelPage;

  const handleTeammateAgentNameUpdate = async (name: string) => {
    try {
      await updateTeammateAgent({
        teammateAgentId,
        payload: { name },
      }).unwrap();
      showToast({
        variant: ToastVariant.Success,
        title: "Agent updated",
        description: "Agent has been updated successfully.",
      });
    } catch (error) {
      console.error("Failed to update teammate agent:", error);
      showToast({
        variant: ToastVariant.Error,
        title: "Failed to update agent",
        description: "Please try again.",
      });
      throw error;
    }
  };

  const handleCopyPlaybookId = async () => {
    const playbookId = teammateAgent?.teammate_agent_config.playbook_id;
    if (!playbookId) {
      return;
    }

    try {
      await navigator.clipboard.writeText(playbookId);
      showToast({
        variant: ToastVariant.Success,
        title: "Playbook ID copied",
        description: "Playbook ID has been copied to clipboard.",
      });
    } catch (error) {
      console.error("Failed to copy playbook ID:", error);
      showToast({
        variant: ToastVariant.Error,
        title: "Failed to copy",
        description: "Please try again.",
      });
    }
  };

  const handleTeammateAgentDelete = async () => {
    try {
      // Navigate back to agents page before deleting so we don't try to view an agent that doesn't exist
      navigate(appPaths.teammateAgents.pattern);

      // Small delay to prevent race condition
      setTimeout(() => {
        void deleteTeammateAgent(teammateAgentId);
      }, 500);

      showToast({
        variant: ToastVariant.Success,
        title: "Agent deleted",
        description: "Agent has been deleted successfully.",
      });
    } catch (error) {
      console.error("Failed to delete teammate agent:", error);
      showToast({
        variant: ToastVariant.Error,
        title: "Failed to delete agent",
        description: "Please try again.",
      });
    }
  };

  if (isLoadingTeammateAgent || !teammateAgent) {
    return null;
  }

  const isStaticAgent = isStaticTeammateAgentIdentity(
    teammateAgent.teammate_agent_config.identity,
  );

  // Static agents author their prompt via the Instructions / Configuration
  // tabs; the legacy Configuration tab doesn't apply to them. The query
  // string rides along so a deep link into the tab survives the redirect.
  if (isStaticAgent && isConfigurationPage) {
    return (
      <Navigate
        to={{
          pathname: appPaths.teammateAgentInstructions.build({
            teammateAgentId,
          }),
          search,
        }}
        replace
      />
    );
  }

  // Legacy agents don't have the static section tabs (and no provider is
  // mounted for them); send them back to the overview.
  if (!isStaticAgent && isStaticSectionsPage) {
    return (
      <Navigate
        to={appPaths.teammateAgentOverview.build({ teammateAgentId })}
        replace
      />
    );
  }

  const header = (
    <PageHeader
      contentClassName="pr-4 pl-2 sm:pr-6 sm:pl-4"
      filterBar={<PageHeaderFilterBarSlot />}
      title={
        <Can
          I={PermissionActions.UPDATE}
          a={PermissionSubjects.TEAMMATES}
          passThrough={true}
        >
          {(allowed) => (
            <EditableText
              accessibleLabel="Agent name"
              editAccessibleLabel="Edit agent name"
              inputClassName="h-7 py-0 text-xl leading-7 font-semibold"
              isDisabled={!allowed}
              maxLength={150}
              minLength={2}
              onSave={handleTeammateAgentNameUpdate}
              value={teammateAgent.name}
            />
          )}
        </Can>
      }
      actions={
        <Can
          I={PermissionActions.DELETE}
          a={PermissionSubjects.TEAMMATES}
          passThrough={true}
        >
          {(allowed) => (
            <AgentMoreMenu
              onClickCopyPlaybookId={handleCopyPlaybookId}
              onClickDeleteAgent={() => setIsDeleteModalOpen(true)}
              isDisabled={!allowed}
            />
          )}
        </Can>
      }
      tabs={
        <RouteTabs
          ariaLabel="Agent sections"
          tabs={
            isStaticAgent
              ? [
                  {
                    label: "Performance",
                    to: "overview",
                    matchDescendants: true,
                  },
                  {
                    label: "Calls",
                    to: "customer-interactions",
                    matchDescendants: true,
                  },
                  { label: "Instructions", to: "instructions" },
                  { label: "Configuration", to: "resources-outputs" },
                  {
                    label: "Testing",
                    to: "testing",
                    matchDescendants: true,
                  },
                  ...(isFunnelTabShown
                    ? [{ label: "Funnel", to: "funnel" }]
                    : []),
                ]
              : [
                  {
                    label: "Overview",
                    to: "overview",
                    matchDescendants: true,
                  },
                  {
                    label: "Calls",
                    to: "customer-interactions",
                    matchDescendants: true,
                  },
                  { label: "Configuration", to: "configuration" },
                  {
                    label: "Testing",
                    to: "testing",
                    matchDescendants: true,
                  },
                ]
          }
        />
      }
    />
  );

  return (
    <TabbedPageLayout header={header}>
      {isStaticAgent ? (
        // The static agent layout keeps the config toolbar mounted while the
        // active tab content swaps inside its own Outlet.
        <StaticAgentConfigLayout
          teammateAgentId={asTeammateId(teammateAgentId)}
        />
      ) : (
        // Each tab owns its layout (a PageBody, or a full-height editor
        // declared via FullHeightPageContent); its filter bar renders into
        // the header's slot via PageHeaderFilterBarPortal.
        <Outlet />
      )}

      <CenteredModal
        isOpen={isDeleteModalOpen}
        onOpenChange={setIsDeleteModalOpen}
        isDismissable
      >
        <AlertDialog
          variant={AlertDialogVariant.Warning}
          title="Delete agent"
          confirmText="Delete agent"
          onConfirm={({ close }) => {
            handleTeammateAgentDelete();
            close();
          }}
        >
          Are you sure you want to delete this agent? This action cannot be
          undone.
        </AlertDialog>
      </CenteredModal>
    </TabbedPageLayout>
  );
};
